import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useMemo,
	useRef,
} from "react";
import { useStore } from "zustand";
import {
	createDrawerStore,
	type DrawerState,
	type DrawerStore,
} from "@/stores/drawer-state.store";

/**
 * Context for the drawer store instance
 */
const DrawerContext = createContext<DrawerStore | null>(null);

/**
 * Provider component that creates and provides a drawer store instance
 * Each drawer should be wrapped with this provider to get isolated state
 */
export function DrawerProvider({ children }: { children: ReactNode }) {
	const storeRef = useRef<DrawerStore>(createDrawerStore());

	// Create store instance only once
	if (!storeRef.current) {
		storeRef.current = createDrawerStore();
	}

	// Reset store when provider unmounts (drawer closes)
	useEffect(() => {
		return () => {
			storeRef.current?.getState().reset();
		};
	}, []);

	return (
		<DrawerContext.Provider value={storeRef.current}>
			{children}
		</DrawerContext.Provider>
	);
}

/**
 * Hook to select specific state from the drawer store
 * Uses fine-grained selectors for optimal performance
 *
 * @example
 * ```tsx
 * const errors = useDrawerStore((state) => state.errors)
 * const canSubmit = useDrawerStore((state) => state.canSubmit)
 * ```
 */
export function useDrawerStore<T>(selector: (state: DrawerState) => T): T {
	const store = useContext(DrawerContext);

	if (!store) {
		throw new Error("useDrawerStore must be used within DrawerProvider");
	}

	return useStore(store, selector);
}

/**
 * Hook to get all drawer actions
 * Returns stable action references that won't cause re-renders
 *
 * @example
 * ```tsx
 * const { addError, clearErrors, setFormState } = useDrawerActions()
 * ```
 */
export function useDrawerActions() {
	const store = useContext(DrawerContext);

	if (!store) {
		throw new Error("useDrawerActions must be used within DrawerProvider");
	}

	// Get the actions once and memoize them to provide stable references
	// This prevents useEffect dependency arrays from triggering unnecessarily
	return useMemo(() => {
		const state = store.getState();
		return {
			setMode: state.setMode,
			addError: state.addError,
			clearErrors: state.clearErrors,
			addSuccess: state.addSuccess,
			clearSuccessMessages: state.clearSuccessMessages,
			setFormState: state.setFormState,
			setSaveHandler: state.setSaveHandler,
			setCancelHandler: state.setCancelHandler,
			reset: state.reset,
		};
	}, [store]);
}

/**
 * Same actions as {@link useDrawerActions}, or `null` outside a DrawerProvider.
 * Use for panels shared by drawer and workspace sidebar.
 */
export function useOptionalDrawerActions() {
	const store = useContext(DrawerContext);

	return useMemo(() => {
		if (!store) return null;
		const state = store.getState();
		return {
			setMode: state.setMode,
			addError: state.addError,
			clearErrors: state.clearErrors,
			addSuccess: state.addSuccess,
			clearSuccessMessages: state.clearSuccessMessages,
			setFormState: state.setFormState,
			setSaveHandler: state.setSaveHandler,
			setCancelHandler: state.setCancelHandler,
			reset: state.reset,
		};
	}, [store]);
}
